import React, { useState, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { DASTicketDetails, EnhancedAttachment } from '../../../../api/types/dasTickets.types';
import { useApprovalActions } from '../../../../hooks/api/das/useApprovalActions';
import {
  useFileUpload,
  useFileUploadSettings,
  formatFileSize,
} from '../../../../hooks/api/das/useAttachments';
import { useActiveRequestTypes } from '../../../../hooks/api/useRequestTypes';
import type { RequestTypeEntity } from '../../../../api/types';
import type { ResubmitRequestRequest } from '../../../../api/types/das/approvalActions.types';

interface ResubmitRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: DASTicketDetails;
  onSuccess?: () => void;
}

export const ResubmitRequestModal: React.FC<ResubmitRequestModalProps> = ({
  isOpen,
  onClose,
  ticket,
  onSuccess,
}) => {
  const [clarificationProvided, setClarificationProvided] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Attachment state — files are auto-added on selection (mirrors
  // NewTicketModal). Title defaults to the filename without extension and
  // can be edited inline on each row; description is optional and likewise
  // edited inline. There is no separate "stage then add" step / `+` button.
  const [attachments, setAttachments] = useState<EnhancedAttachment[]>([]);

  // Preview state for viewing attachments
  const [previewAttachment, setPreviewAttachment] = useState<{
    url: string;
    fileName: string;
    fileType: string;
  } | null>(null);

  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { resubmit } = useApprovalActions();
  // File upload limits — combines global defaults with per-request-type
  // overrides (admin.tbl_request_type.max_file_size_kb / max_total_size_kb /
  // allowed_extensions). Resubmits must respect the same limits the user
  // saw at create time, so we mirror NewTicketModal's pattern.
  const { limits: fileUploadLimits } = useFileUploadSettings();
  const { requestTypes } = useActiveRequestTypes();

  const selectedRequestType = useMemo<RequestTypeEntity | null>(
    () => requestTypes.find((rt) => rt.id === ticket.requestTypeId) ?? null,
    [requestTypes, ticket.requestTypeId]
  );

  const effectiveFileLimits = useMemo(() => {
    if (!selectedRequestType) return fileUploadLimits;
    return {
      maxIndividualSize: selectedRequestType.maxFileSizeKB
        ? selectedRequestType.maxFileSizeKB * 1024
        : fileUploadLimits.maxIndividualSize,
      maxTotalSize: selectedRequestType.maxTotalSizeKB
        ? selectedRequestType.maxTotalSizeKB * 1024
        : fileUploadLimits.maxTotalSize,
      allowedExtensions: selectedRequestType.allowedExtensions
        ? selectedRequestType.allowedExtensions
        : fileUploadLimits.allowedExtensions,
      allowedExtensionsDisplay: selectedRequestType.allowedExtensions
        ? selectedRequestType.allowedExtensions.join(', ')
        : fileUploadLimits.allowedExtensionsDisplay,
    };
  }, [selectedRequestType, fileUploadLimits]);

  const { uploadFile, validateFile, getFormattedTotalSize, fileSizeLimits } = useFileUpload(effectiveFileLimits);

  // Find the next approver from approval hierarchy
  const nextApproverInfo = useMemo(() => {
    // First try to use the nextApprover field if available
    if (ticket.nextApprover) {
      return {
        name: ticket.nextApprover,
        level: undefined,
        levelName: undefined,
      };
    }

    // Otherwise, find from approval hierarchy
    if (ticket.approvalHierarchy && ticket.approvalHierarchy.length > 0) {
      // Find the first pending level (where request will be routed to)
      const pendingLevel = ticket.approvalHierarchy.find(
        (level) => level.status === 'Pending'
      );
      if (pendingLevel) {
        return {
          name: pendingLevel.approverName,
          level: pendingLevel.level,
          levelName: pendingLevel.levelName,
        };
      }
    }

    return null;
  }, [ticket.nextApprover, ticket.approvalHierarchy]);

  // Clean up the return reason text from backend format
  // Backend format: "[CategoryName] ClarificationRequired. Comments"
  // We want to: 1) Remove category prefix, 2) Remove duplicate text
  const cleanReturnReason = (reason: string | undefined): string => {
    if (!reason) return '';

    let cleaned = reason;

    // Remove category prefix like "[Clarification Needed]", "[Missing Attachments]", etc.
    cleaned = cleaned.replace(/^\[[\w\s]+\]\s*/i, '');

    // Split by ". " and remove duplicates while preserving order
    const parts = cleaned.split('. ').filter(Boolean);
    const uniqueParts: string[] = [];
    const seen = new Set<string>();

    for (const part of parts) {
      const normalized = part.trim().toLowerCase();
      if (!seen.has(normalized) && part.trim()) {
        seen.add(normalized);
        uniqueParts.push(part.trim());
      }
    }

    return uniqueParts.join('. ');
  };

  // Find the return reason from workflow history and approval hierarchy
  // Strategy: Use workflow history for WHO returned and WHEN, but use approval hierarchy for the actual COMMENT
  // because workflow history has system-generated text while approval hierarchy has the user's actual comment
  const returnInfo = useMemo(() => {
    let returnedBy: string | undefined;
    let returnedByRole: string | undefined;
    let returnedDate: string | undefined;
    let reason: string | undefined;

    // First, get returnedBy/role/date from workflow history (authoritative for who returned and when)
    if (ticket.workflowHistory && ticket.workflowHistory.length > 0) {
      const returnedActions = ticket.workflowHistory.filter(
        (action) => {
          const actionLower = action.action.toLowerCase();
          return actionLower === 'returned' || actionLower === 'returnedforclarification';
        }
      );

      if (returnedActions.length > 0) {
        const latestReturn = returnedActions[returnedActions.length - 1];
        returnedBy = latestReturn.actionBy || latestReturn.actionByName;
        returnedByRole = latestReturn.actionByRole;
        returnedDate = latestReturn.actionDate;
        // Don't use workflow history comments - they contain system-generated text
      }
    }

    // Get the actual user comment from approval hierarchy
    // The approval hierarchy has the actual comment entered by the approver
    if (ticket.approvalHierarchy && ticket.approvalHierarchy.length > 0) {
      // Find the approver who returned (by matching name if we found it from workflow history)
      // OR find the most recent approver with comments and actionDate
      let matchingLevel;

      if (returnedBy) {
        // Try to find the exact approver who returned
        matchingLevel = ticket.approvalHierarchy.find(
          (level) => level.approverName === returnedBy && level.comments && level.actionDate
        );
      }

      // If no exact match, find the most recent level with comments and actionDate
      if (!matchingLevel) {
        const levelsWithComments = ticket.approvalHierarchy.filter(
          (level) => level.comments && level.actionDate
        );

        if (levelsWithComments.length > 0) {
          const sortedLevels = [...levelsWithComments].sort((a, b) => {
            if (a.actionDate && b.actionDate) {
              return new Date(b.actionDate).getTime() - new Date(a.actionDate).getTime();
            }
            return 0;
          });
          matchingLevel = sortedLevels[0];
        }
      }

      if (matchingLevel) {
        reason = matchingLevel.comments;
        // If we didn't get returnedBy from workflow history, use approval hierarchy
        if (!returnedBy) {
          returnedBy = matchingLevel.approverName;
          returnedByRole = matchingLevel.approverRole;
          returnedDate = matchingLevel.actionDate;
        }
      }
    }

    if (!returnedBy && !reason) {
      return null;
    }

    return {
      returnedBy,
      returnedByRole,
      returnedDate,
      reason: cleanReturnReason(reason),
    };
  }, [ticket.workflowHistory, ticket.approvalHierarchy]);

  // Format date for display
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle file selection
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate extension first (best UX — fail fast before size check).
      if (fileSizeLimits.allowedExtensions && fileSizeLimits.allowedExtensions.length > 0) {
        const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
        const allowed = fileSizeLimits.allowedExtensions.map((ext) => ext.toLowerCase());
        if (!allowed.includes(fileExt)) {
          alert(
            `File type "${fileExt}" is not allowed. Allowed types: ${
              fileSizeLimits.allowedExtensionsDisplay || allowed.join(', ')
            }`
          );
          e.target.value = '';
          return;
        }
      }

      // Validate file size + cumulative total against the effective limits.
      const existingFiles = attachments.map((a) => a.file);
      const validation = validateFile(file, existingFiles);

      if (!validation.isValid) {
        alert(validation.errorMessage);
        e.target.value = '';
        return;
      }

      // Auto-add. Default title = filename without extension; admin/user can
      // edit it inline on the attachment row. Mirrors NewTicketModal.
      const defaultTitle = file.name.replace(/\.[^/.]+$/, '');
      const enhancedAttachment: EnhancedAttachment = {
        file,
        title: defaultTitle,
        description: '',
      };
      setAttachments((prev) => [...prev, enhancedAttachment]);
      setError(null);

      // Reset the input so the same file (or another) can be picked again.
      e.target.value = '';
    }
  }, [attachments, validateFile, fileSizeLimits]);

  // Inline edit of title/description on an existing attachment row.
  const handleUpdateAttachment = useCallback(
    (index: number, field: 'title' | 'description', value: string) => {
      setAttachments((prev) =>
        prev.map((att, i) => (i === index ? { ...att, [field]: value } : att))
      );
    },
    []
  );

  // Remove attachment from list
  const handleRemoveAttachment = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // View/Preview attachment
  const handleViewAttachment = useCallback((attachment: EnhancedAttachment) => {
    const url = URL.createObjectURL(attachment.file);
    setPreviewAttachment({
      url,
      fileName: attachment.file.name,
      fileType: attachment.file.type,
    });
  }, []);

  // Close preview
  const closePreview = useCallback(() => {
    if (previewAttachment?.url) {
      URL.revokeObjectURL(previewAttachment.url);
    }
    setPreviewAttachment(null);
  }, [previewAttachment]);

  if (!isOpen) return null;

  const handleSubmitClick = () => {
    if (!clarificationProvided.trim()) {
      setError('Please provide clarification details');
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmDialog(false);
    setIsSubmitting(true);
    setError(null);

    const request: ResubmitRequestRequest = {
      clarificationProvided: clarificationProvided.trim(),
      rowVersion: parseInt(ticket.rowVersion || '0', 10),
    };

    try {
      const result = await resubmit(String(ticket.id), request);
      if (result) {
        // Upload attachments if any
        if (attachments.length > 0) {
          const uploadPromises = attachments.map((attachment) =>
            uploadFile(attachment.file, {
              requestId: ticket.id,
              documentTitle: attachment.title,
              documentDescription: attachment.description || undefined,
            })
          );

          const uploadResults = await Promise.all(uploadPromises);
          const failedUploads = uploadResults.filter((r) => r === null);

          if (failedUploads.length > 0) {
            console.warn(`${failedUploads.length} attachment(s) failed to upload`);
          }
        }

        onSuccess?.();
        onClose();
      } else {
        setError('Failed to resubmit request. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (previewAttachment) {
        closePreview();
      } else if (!isSubmitting && !showConfirmDialog) {
        onClose();
      }
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001,
        padding: '1rem',
      }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={handleKeyDown}
    >
      <div
        style={{
          background: 'var(--bg-primary)',
          borderRadius: '0.75rem',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.5rem',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '0.5rem',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconSystem.refresh size={20} style={{ color: 'white' }} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                Resubmit Request
              </h2>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                {ticket.ticketNo}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              background: 'none',
              border: 'none',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: 'var(--text-secondary)',
              opacity: isSubmitting ? 0.5 : 1,
            }}
            title="Close"
          >
            <IconSystem.x size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem', overflow: 'auto', flex: 1 }}>
          {/* Consolidated Return Info Card */}
          <div
            style={{
              background: 'rgba(245, 158, 11, 0.08)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '0.5rem',
              padding: '1rem',
              marginBottom: '1.5rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
              <IconSystem.alert size={20} style={{ color: '#f59e0b', flexShrink: 0, marginTop: '2px' }} />
              <div style={{ flex: 1 }}>
                {/* Header with returned by info */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-primary)', fontWeight: 600 }}>
                    Request Returned for Clarification
                  </p>
                  {returnInfo?.returnedBy && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      {returnInfo.returnedDate && formatDate(returnInfo.returnedDate)}
                    </span>
                  )}
                </div>

                {/* Return reason */}
                {returnInfo?.reason && (
                  <div
                    style={{
                      padding: '0.75rem',
                      background: 'rgba(245, 158, 11, 0.1)',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      color: 'var(--text-primary)',
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                      marginBottom: '0.75rem',
                    }}
                  >
                    {returnInfo.reason}
                  </div>
                )}

                {/* Returned by and Resubmitting to info */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.8125rem' }}>
                  {returnInfo?.returnedBy && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                      <IconSystem.user size={14} style={{ color: '#f59e0b' }} />
                      <span style={{ color: 'var(--text-secondary)' }}>Returned by:</span>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                        {returnInfo.returnedBy}
                        {returnInfo.returnedByRole && (
                          <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}> ({returnInfo.returnedByRole})</span>
                        )}
                      </span>
                    </div>
                  )}
                  {nextApproverInfo && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                      <IconSystem.refresh size={14} style={{ color: '#3b82f6' }} />
                      <span style={{ color: 'var(--text-secondary)' }}>Resubmitting to:</span>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{nextApproverInfo.name}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div
              style={{
                background: 'rgba(220, 38, 38, 0.1)',
                border: '1px solid rgba(220, 38, 38, 0.2)',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <IconSystem.alert size={18} style={{ color: '#dc2626' }} />
              <span style={{ fontSize: '0.875rem', color: '#dc2626' }}>{error}</span>
            </div>
          )}

          {/* Clarification Field */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
                marginBottom: '0.5rem',
              }}
            >
              Clarification Provided <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <textarea
              value={clarificationProvided}
              onChange={(e) => setClarificationProvided(e.target.value)}
              placeholder="Provide the clarification or additional information requested by the approver..."
              disabled={isSubmitting}
              style={{
                width: '100%',
                minHeight: '150px',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                color: 'var(--text-primary)',
                background: 'var(--bg-primary)',
                resize: 'vertical',
                fontFamily: 'inherit',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Attachments Section */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
                marginBottom: '0.5rem',
              }}
            >
              Attachments (Optional)
            </label>

            {/* File Size Limits Info */}
            <div
              style={{
                padding: '0.5rem 0.75rem',
                background: 'rgba(59, 130, 246, 0.08)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '0.375rem',
                marginBottom: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.75rem',
                color: '#3b82f6',
              }}
            >
              <IconSystem.info size={14} />
              <span>
                Max file size: <strong>{formatFileSize(fileSizeLimits.maxIndividualSize)}</strong> per file |
                Total limit: <strong>{formatFileSize(fileSizeLimits.maxTotalSize)}</strong>
              </span>
            </div>

            {/* File picker — auto-adds on selection. No "+" button. */}
            <div style={{ marginBottom: '1rem' }}>
              <input
                id="resubmit-file-upload"
                type="file"
                onChange={handleFileChange}
                disabled={isSubmitting}
                style={{
                  fontSize: '0.875rem',
                  color: 'var(--text-primary)',
                  width: '100%',
                }}
              />
            </div>

            {/* Attachments List */}
            {attachments.length > 0 ? (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {attachments.map((attachment, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                        padding: '0.75rem',
                        background: 'var(--bg-secondary)',
                        borderRadius: '0.375rem',
                        border: '1px solid var(--border-color)',
                      }}
                    >
                      {/* Row 1: file metadata + view/delete actions */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '0.5rem',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                          <IconSystem.file size={20} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                          <div
                            style={{
                              minWidth: 0,
                              flex: 1,
                              fontSize: '0.8125rem',
                              color: 'var(--text-secondary)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {attachment.file.name} ({formatFileSize(attachment.file.size)})
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          {(attachment.file.type.startsWith('image/') || attachment.file.type.includes('pdf')) && (
                            <button
                              type="button"
                              onClick={() => handleViewAttachment(attachment)}
                              disabled={isSubmitting}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#3b82f6',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                padding: '0.25rem',
                                display: 'flex',
                                alignItems: 'center',
                                opacity: isSubmitting ? 0.5 : 1,
                              }}
                              title="View attachment"
                            >
                              <IconSystem.eye size={18} />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemoveAttachment(index)}
                            disabled={isSubmitting}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#dc2626',
                              cursor: isSubmitting ? 'not-allowed' : 'pointer',
                              padding: '0.25rem',
                              display: 'flex',
                              alignItems: 'center',
                              opacity: isSubmitting ? 0.5 : 1,
                            }}
                            title="Remove attachment"
                          >
                            <IconSystem.trash size={18} />
                          </button>
                        </div>
                      </div>
                      {/* Row 2: inline editable title + description (same as NewTicketModal) */}
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                          type="text"
                          placeholder="Title *"
                          value={attachment.title}
                          onChange={(e) => handleUpdateAttachment(index, 'title', e.target.value)}
                          disabled={isSubmitting}
                          style={{
                            flex: 1,
                            padding: '0.4rem 0.625rem',
                            border: '1px solid var(--border-color)',
                            borderRadius: '0.375rem',
                            fontSize: '0.8125rem',
                            color: 'var(--text-primary)',
                            background: 'var(--bg-primary)',
                          }}
                        />
                        <input
                          type="text"
                          placeholder="Description (Optional)"
                          value={attachment.description || ''}
                          onChange={(e) => handleUpdateAttachment(index, 'description', e.target.value)}
                          disabled={isSubmitting}
                          style={{
                            flex: 1,
                            padding: '0.4rem 0.625rem',
                            border: '1px solid var(--border-color)',
                            borderRadius: '0.375rem',
                            fontSize: '0.8125rem',
                            color: 'var(--text-primary)',
                            background: 'var(--bg-primary)',
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {/* Total Size Summary */}
                <div
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    background: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: '0.375rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '0.75rem',
                  }}
                >
                  <span style={{ color: '#15803d' }}>
                    Total: {attachments.length} file(s)
                  </span>
                  <span style={{ fontWeight: 600, color: '#15803d' }}>
                    {getFormattedTotalSize(attachments.map((a) => a.file))} / {formatFileSize(fileSizeLimits.maxTotalSize)}
                  </span>
                </div>
              </>
            ) : (
              <div
                style={{
                  padding: '1rem',
                  textAlign: 'center',
                  color: 'var(--text-tertiary)',
                  fontSize: '0.875rem',
                  background: 'var(--bg-secondary)',
                  borderRadius: '0.375rem',
                  border: '1px dashed var(--border-color)',
                }}
              >
                No attachments added
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
          }}
        >
          <button
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              padding: '0.625rem 1.25rem',
              background: 'transparent',
              border: '1px solid var(--border-color)',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'var(--text-primary)',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmitClick}
            disabled={isSubmitting || !clarificationProvided.trim()}
            style={{
              padding: '0.625rem 1.25rem',
              background: isSubmitting || !clarificationProvided.trim() ? '#9ca3af' : '#3b82f6',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'white',
              cursor: isSubmitting || !clarificationProvided.trim() ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            {isSubmitting ? (
              <>
                <span
                  style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid white',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                Resubmitting...
              </>
            ) : (
              <>
                <IconSystem.refresh size={16} />
                Resubmit Request
              </>
            )}
          </button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10002,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'rgba(59, 130, 246, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconSystem.refresh size={20} style={{ color: '#3b82f6' }} />
              </div>
              <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                Confirm Resubmission
              </h3>
            </div>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Are you sure you want to resubmit this request?
              {nextApproverInfo && (
                <>
                  {' '}It will be sent to <strong style={{ color: 'var(--text-primary)' }}>{nextApproverInfo.name}</strong>
                  {nextApproverInfo.level !== undefined && (
                    <> (Level {nextApproverInfo.level})</>
                  )}
                  {' '}for review.
                </>
              )}
            </p>
            {attachments.length > 0 && (
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                {attachments.length} attachment{attachments.length > 1 ? 's' : ''} will be uploaded with this request.
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                onClick={() => setShowConfirmDialog(false)}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSubmit}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#3b82f6',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <IconSystem.check size={16} />
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attachment Preview Modal */}
      {previewAttachment && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            closePreview();
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 11000,
            padding: '2rem',
          }}
        >
          {/* Preview Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              color: 'white',
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Attachment Preview</h3>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', opacity: 0.8 }}>
                {previewAttachment.fileName}
              </p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                closePreview();
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: 'white',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Close preview"
            >
              <IconSystem.x size={24} />
            </button>
          </div>

          {/* Preview Content */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              flex: 1,
              background: 'white',
              borderRadius: '0.5rem',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {previewAttachment.fileType.startsWith('image/') ? (
              <img
                src={previewAttachment.url}
                alt={previewAttachment.fileName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : previewAttachment.fileType.includes('pdf') ? (
              <iframe
                src={previewAttachment.url}
                title={previewAttachment.fileName}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
              />
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                <IconSystem.file size={48} style={{ marginBottom: '1rem' }} />
                <p>Preview not available for this file type</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Spinner Animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ResubmitRequestModal;
